import { makeAutoObservable, configure, runInAction } from 'mobx';
import { strictEmailRegex } from '@JohnLewisPartnership/jl-design-system/dist/form/regex';
import { sendNotificationEmail } from '../../helpers/onlineStock/notificationEmail';

// Add support for browsers without Proxy object support
configure({ useProxies: 'ifavailable' });

class EmailModalStore {
    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    customerEmail = '';
    selectedProduct: Product | undefined = undefined;
    trackedProducts: string[] = [];
    emailMeSubmitting = false;
    emailFieldTouched = false;
    customerNotifiableEvents: CustomerNotifiableEvents | undefined = undefined;

    get isEmailMeModalOpen(): boolean {
        return this.selectedProduct !== undefined && !this.trackedProducts.includes(this.selectedProduct.skuId);
    }

    openEmailModal = (product: Product, customerNotifiableEvents: CustomerNotifiableEvents): void => {
        runInAction(() => {
            this.emailFieldTouched = false;
            this.selectedProduct = product;
            this.getAndSetCustomerEmail();
            this.customerNotifiableEvents = customerNotifiableEvents;
        });
    };

    getAndSetCustomerEmail(): void {
        const customerEmail = window.listingAuth0Claims?.['https://www.johnlewis.com/auth/email'];
        if (customerEmail) {
            this.customerEmail = customerEmail;
        }
    }

    closeEmailMeModal(): void {
        runInAction(() => {
            this.selectedProduct = undefined;
            this.customerNotifiableEvents = undefined;
            this.customerEmail = '';
            this.emailFieldTouched = false;
        });
    }

    get emailMeEmailError(): string {
        if (!this.emailFieldTouched) return '';

        if (!this.customerEmail) {
            return 'Please enter an email address';
        }

        const isValid = strictEmailRegex.test(this.customerEmail);
        if (!isValid) {
            return 'Please make sure your email format is correct';
        }

        return '';
    }

    get showEmailMeError(): boolean {
        return this.emailFieldTouched && !!this.emailMeEmailError;
    }

    toggleEmailFieldTouched = (): void => {
        // timeout prevents error from showing on close of modal
        setTimeout(
            () =>
                runInAction(() => {
                    this.emailFieldTouched = true;
                }),
            300,
        );
    };

    async emailMe(): Promise<void> {
        const { selectedProduct } = this;
        runInAction(() => {
            this.emailFieldTouched = true;
        });

        if (
            this.showEmailMeError ||
            !this.customerNotifiableEvents ||
            !selectedProduct ||
            !selectedProduct.productId ||
            !selectedProduct.skuId
        ) {
            return;
        }

        try {
            runInAction(() => {
                this.emailMeSubmitting = true;
            });

            const email = this.customerEmail;
            const productId = selectedProduct.productId;
            const skuId = selectedProduct.skuId;
            const notificationEmailResponse = await sendNotificationEmail(
                email,
                skuId,
                productId,
                this.customerNotifiableEvents,
            );

            if (notificationEmailResponse.success) {
                runInAction(() => {
                    this.trackedProducts.push(selectedProduct.skuId);
                });
            }
        } finally {
            runInAction(() => {
                this.emailMeSubmitting = false;
                // swallow any errors and close modal so that customer journey is not impacted
                this.closeEmailMeModal();
            });
        }
    }
}

export default EmailModalStore;
